import { useEffect, useRef, useState, type Dispatch, type SetStateAction } from "react";
import { Alert, Button, Checkbox, FormControlLabel, IconButton, MenuItem, Stack, Switch, TextField, Typography } from "@mui/material";
import { SelectEntidade } from "@/components/SelectEntidade";
import { Add, Delete, Edit, Group, PlayArrow, SwapHoriz, Sync } from "@mui/icons-material";
import { FilterModal } from "@/components/FilterModal";
import usePagination from "@/hooks/pagination";
import { ConfirmModal } from "@/components/ConfirmModal";
import { useNotification } from "@/context/NotificationContext";
import { useCrud } from "@/hooks/useCrud";
import { DataTable } from "@/components/DataTable";
import { CrudModal } from "@/components/crud/CrudModal";
import { CrudToolbar } from "@/components/crud/CrudToolbar";
import { EscalaVinculoModal, validarVinculoValues, type EscalaVinculoValues } from "@/components/escala/EscalaVinculoModal";
import { EscalaResponsavelModal } from "@/components/escala/EscalaResponsavelModal";
import { EscalaGerarModal, validarGerarValues, type GerarValues } from "@/components/escala/EscalaGerarModal";
import { useLogger } from "@/hooks/useLogger";
import { useFilterState } from "@/hooks/useFilterState";
import { useOpcoesCadastro } from "@/hooks/useOpcoesCadastro";
import { LogEventType } from "@/api/log.service";
import { useAuth } from "@/context/AuthContext";
import { capitalizar } from "@/utils/format";
import {
  motivoCargaIncompativel,
  motivoCurtoCargaIncompativel,
  opcoesDeCargaDaEscala,
  rotuloCarga,
  validarCargasDaEscala,
} from "@/components/escala/cargaHoraria";
import {
  REGRA_CARGA_SEM_NINGUEM,
  REGRA_DOMINGO_ACIMA_DO_TETO,
  REGRA_FOLGAS_SEGUIDAS,
  REGRA_GRUPO_DE_FOLGA_POR_LEI,
  REGRA_HORAS_DA_SEMANA,
  REGRA_PRESENCA_ABAIXO_DO_MINIMO,
} from "@/components/escala/espelho";
import {
  DIA_SEMANA_LABEL,
  ESCALA_TIPOS,
  EscalaCaracteristicaLabels,
  EscalaTipoLabels,
  excluirEscala,
  gerarEscala,
  liberarForaPrazo,
  listarTodosColaboradores,
  listCargasHorarias,
  listEscalas,
  listGeradas,
  listLojas,
  listVinculosRegulares,
  lojaLabel,
  obterEscala,
  saveEscala,
  saveVinculos,
  transferirResponsavel,
  formatDataHoraBR,
  rotuloLoja,
  type CargaHoraria,
  type Colaborador,
  type Escala,
  type EscalaCaracteristica,
  type EscalaTipo,
  type Impedimento,
  type Loja,
  type VinculoRegularAtivo,
} from "@/api/escala.service";

interface EscalaFormProps {
  values: Partial<Escala>;
  setValues: Dispatch<SetStateAction<Partial<Escala>>>;
  isEditing: boolean;
  lojas: Loja[];
  cargas: CargaHoraria[];
  // Cargas da escala salva: as que foram inativadas depois continuam valendo nela.
  cargasSalvas: string[];
  onToggleLiberarForaPrazo: (escalaId: string, liberar: boolean) => Promise<Escala | null>;
}

// Liberação fora do prazo (docs/escala/05-plano.md D5) fica dentro do modal
// de editar escala, não numa coluna da grid — é exceção de governança, não
// dado do cadastro em si. Não existe gate de papel aqui na tela: quem não é
// Gerente vê o toggle igual a todo mundo e recebe o erro do backend ao
// tentar usá-lo, mesmo padrão do resto da Escala (EscalaPage.tsx: "não há
// gate por papel aqui na tela, igual às demais"). Só aparece editando —
// uma escala nova ainda não tem id pra chamar escala.liberar_fora_prazo.
function EscalaForm({ values, setValues, isEditing, lojas, cargas, cargasSalvas, onToggleLiberarForaPrazo }: EscalaFormProps) {
  const mostraDiaFolgaFixa = values.tipo === "FOLGA_FIXA";
  const travado = !!values.tem_geracao_viva;
  const [alternando, setAlternando] = useState(false);

  // A carga que deixou de servir (o tipo mudou depois da escolha) fica no campo com o motivo, para
  // quem salva decidir entre trocar a carga ou voltar o tipo.
  const opcoesCarga = opcoesDeCargaDaEscala(cargas, cargasSalvas);
  const idsCarga = values.cargas_horarias_ids ?? [];
  const erroCarga = values.tipo
    ? cargas
        .filter((c) => idsCarga.includes(c.id!))
        .map((c) => motivoCargaIncompativel(c, values.tipo!, values.dia_folga_fixa))
        .find(Boolean)
    : undefined;

  const handleToggleLiberacao = async (liberar: boolean) => {
    if (!values.id) return;
    setAlternando(true);
    try {
      const result = await onToggleLiberarForaPrazo(values.id, liberar);
      if (result) {
        setValues((v) => ({ ...v, liberar_fora_prazo: result.liberar_fora_prazo, liberado_em: result.liberado_em }));
      }
    } finally {
      setAlternando(false);
    }
  };

  return (
    <>
      <TextField
        label="Nome"
        fullWidth
        required
        value={values.nome ?? ""}
        onChange={(e) => setValues((v) => ({ ...v, nome: e.target.value }))}
        slotProps={{ htmlInput: { maxLength: 200 } }}
      />

      {travado && (
        <Alert severity="info">
          Esta escala tem geração ativa: loja, tipo, característica, turno, dia de folga fixa e carga
          horária só podem ser alterados depois de cancelar as gerações.
        </Alert>
      )}

      <SelectEntidade
        label="Loja"
        options={lojas}
        value={values.loja_id}
        onChange={(id) => setValues((v) => ({ ...v, loja_id: id ?? undefined }))}
        getLabel={lojaLabel}
        required
        disabled={travado}
      />

      <TextField
        select
        label="Tipo"
        fullWidth
        required
        disabled={travado}
        value={values.tipo ?? ""}
        onChange={(e) => {
          const tipo = e.target.value as EscalaTipo;
          setValues((v) => ({ ...v, tipo, dia_folga_fixa: tipo === "FOLGA_FIXA" ? v.dia_folga_fixa : null }));
        }}
      >
        {ESCALA_TIPOS.map((t) => (
          <MenuItem key={t.value} value={t.value}>
            {t.label}
          </MenuItem>
        ))}
      </TextField>

      {mostraDiaFolgaFixa && (
        <TextField
          select
          label="Dia de folga fixa"
          fullWidth
          required
          disabled={travado}
          value={values.dia_folga_fixa ?? ""}
          onChange={(e) => setValues((v) => ({ ...v, dia_folga_fixa: Number(e.target.value) }))}
        >
          {Object.entries(DIA_SEMANA_LABEL).map(([dia, label]) => (
            <MenuItem key={dia} value={dia}>
              {label}
            </MenuItem>
          ))}
        </TextField>
      )}

      <Stack direction="row" spacing={2}>
        <TextField
          select
          label="Característica"
          fullWidth
          disabled={travado}
          value={values.caracteristica ?? "REGULAR"}
          onChange={(e) => setValues((v) => ({ ...v, caracteristica: e.target.value as EscalaCaracteristica }))}
        >
          {(Object.keys(EscalaCaracteristicaLabels) as EscalaCaracteristica[]).map((c) => (
            <MenuItem key={c} value={c}>
              {EscalaCaracteristicaLabels[c]}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Turno"
          fullWidth
          disabled={travado}
          value={values.turno ?? "GERAL"}
          onChange={(e) => setValues((v) => ({ ...v, turno: e.target.value }))}
          slotProps={{ htmlInput: { maxLength: 20 } }}
        />
      </Stack>

      <FormControlLabel
        control={
          <Checkbox
            checked={!!values.com_carga_horaria}
            disabled={travado}
            onChange={(e) => setValues((v) => ({ ...v, com_carga_horaria: e.target.checked }))}
          />
        }
        label="Escala com carga horária"
      />
      {values.com_carga_horaria && (
        <SelectEntidade
          multiple
          label="Cargas horárias da escala"
          options={opcoesCarga}
          value={idsCarga}
          onChange={(ids) => setValues((v) => ({ ...v, cargas_horarias_ids: ids }))}
          getLabel={rotuloCarga}
          getDisabledReason={(c) =>
            values.tipo ? motivoCurtoCargaIncompativel(c, values.tipo, values.dia_folga_fixa) : undefined
          }
          required
          disabled={travado}
          error={!!erroCarga}
          helperText={erroCarga ? capitalizar(erroCarga) : undefined}
        />
      )}

      {isEditing && (
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
          <FormControlLabel
            control={
              <Switch
                checked={!!values.liberar_fora_prazo}
                onChange={(e) => handleToggleLiberacao(e.target.checked)}
                disabled={alternando}
              />
            }
            label="Liberar fora do prazo"
          />
          {values.liberar_fora_prazo && values.liberado_em && (
            <Typography variant="caption" color="text.secondary">
              Liberado em {formatDataHoraBR(values.liberado_em)}
            </Typography>
          )}
        </Stack>
      )}
    </>
  );
}

export function EscalaEscalasTab() {
  const { user } = useAuth();
  const clientId = user!.loggedClient!.id;
  const { notify } = useNotification();
  const { log } = useLogger();
  const { total, setTotal, page, setPage, rowsPerPage, setRowsPerPage } = usePagination();
  const { open, editing, handleCreate, handleEdit, handleClose } = useCrud<Escala>();

  const [escalas, setEscalas] = useState<Escala[]>([]);
  // Só vira true na primeira carga e nunca mais volta — recargas seguintes não trocam
  // toolbar/tabela por um spinner de página inteira (R47, mesmo padrão de useListaRemota).
  const [carregouAlgumaVez, setCarregouAlgumaVez] = useState(false);
  const chamada = useRef(0);

  const [excluindo, setExcluindo] = useState<Escala | null>(null);
  const [transferindo, setTransferindo] = useState<Escala | null>(null);

  const { inputValues, setInputValues, filtros, applyFilters, handleClearFilters } = useFilterState(
    { loja_id: "", nome: "" },
    () => setPage(0),
  );

  const [vinculando, setVinculando] = useState<Escala | null>(null);
  const [vinculoInitialData, setVinculoInitialData] = useState<EscalaVinculoValues | null>(null);
  const [colaboradoresDaLoja, setColaboradoresDaLoja] = useState<Colaborador[]>([]);
  const [vinculosRegulares, setVinculosRegulares] = useState<VinculoRegularAtivo[]>([]);
  const [carregandoVinculoId, setCarregandoVinculoId] = useState<string | null>(null);

  const [gerando, setGerando] = useState<Escala | null>(null);
  const [colaboradoresDaEquipe, setColaboradoresDaEquipe] = useState<Colaborador[]>([]);
  const [semCargaDaEquipe, setSemCargaDaEquipe] = useState<Colaborador[]>([]);
  // Escala com carga em que nenhum vinculado tem uma das cargas: a geração é recusada.
  const [vinculadosSemCarga, setVinculadosSemCarga] = useState(false);
  const [jornadaAnterior, setJornadaAnterior] = useState<number | undefined>(undefined);
  const [carregandoGerarId, setCarregandoGerarId] = useState<string | null>(null);

  // Cada lista é resolvida de forma independente (useOpcoesCadastro) — um
  const { opcoes: { lojas, cargas } } = useOpcoesCadastro<{
    lojas: Loja[];
    cargas: CargaHoraria[];
  }>(
    {
      lojas: { buscar: () => listLojas(clientId), mensagemErro: "Erro ao carregar lojas" },
      cargas: { buscar: () => listCargasHorarias(clientId), mensagemErro: "Erro ao carregar cargas horárias" },
    },
    [clientId],
  );

  const loadData = async () => {
    const minha = ++chamada.current;
    try {
      const result = await listEscalas(
        {
          loja_id: filtros.loja_id || undefined,
          nome: filtros.nome || undefined,
          page: page + 1,
          page_size: rowsPerPage,
        },
        clientId,
      );
      if (minha !== chamada.current) return;
      setEscalas(result.items ?? []);
      setTotal(result.total ?? 0);
    } catch (error) {
      if (minha === chamada.current) notify("Erro ao carregar escalas", "error");
    } finally {
      if (minha === chamada.current) setCarregouAlgumaVez(true);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, filtros, page, rowsPerPage]);

  const activeFilterCount = Object.values(filtros).filter((v) => v).length;

  const onSave = async (values: Partial<Escala>) => {
    if (!values.nome?.trim()) {
      notify("Nome é obrigatório", "error");
      return;
    }
    if (!values.loja_id) {
      notify("Loja é obrigatória", "error");
      return;
    }
    if (!values.tipo) {
      notify("Tipo é obrigatório", "error");
      return;
    }
    if (values.tipo === "FOLGA_FIXA" && !values.dia_folga_fixa) {
      notify("Dia de folga fixa é obrigatório para esse tipo", "error");
      return;
    }
    const diaFolgaFixa = values.tipo === "FOLGA_FIXA" ? values.dia_folga_fixa : null;
    const erroCarga = validarCargasDaEscala({ ...values, dia_folga_fixa: diaFolgaFixa }, cargas, editing?.cargas_horarias_ids);
    if (erroCarga) {
      notify(capitalizar(erroCarga), "error");
      return;
    }

    try {
      const result = await saveEscala(
        {
          id: editing?.id,
          loja_id: values.loja_id,
          nome: values.nome,
          tipo: values.tipo,
          caracteristica: values.caracteristica || undefined,
          turno: values.turno || undefined,
          dia_folga_fixa: diaFolgaFixa,
          com_carga_horaria: !!values.com_carga_horaria,
          cargas_horarias_ids: values.com_carga_horaria ? values.cargas_horarias_ids ?? [] : [],
        },
        clientId,
      );

      log(editing ? LogEventType.Updated : LogEventType.Created, result, "escala_escala", result.id, "info");
      notify(`Escala ${editing ? "atualizada" : "criada"}!`, "success");
      loadData();
      handleClose();
    } catch (e: any) {
      notify(e?.message || "Erro ao salvar escala", "error");
    }
  };

  // escala.liberar_fora_prazo é um Method à parte de salvar_escala (papel
  // mínimo Gerente, 04-contratos.md) — chamado direto do toggle, não
  // esperando o botão "Salvar" do modal, mesmo comportamento imediato do
  // checkbox equivalente no painel de parâmetros do Vitruvio de origem.
  const handleToggleLiberarForaPrazo = async (escalaId: string, liberar: boolean): Promise<Escala | null> => {
    try {
      const result = await liberarForaPrazo(escalaId, liberar, clientId);
      log(LogEventType.Updated, result, "escala_escala", escalaId, "info");
      notify(liberar ? "Liberação fora do prazo concedida!" : "Liberação fora do prazo revogada!", "success");
      loadData();
      return result;
    } catch (e: any) {
      notify(e?.message || "Erro ao alterar liberação fora do prazo", "error");
      return null;
    }
  };

  const onExcluirConfirm = async () => {
    if (!excluindo?.id) return;
    try {
      await excluirEscala(excluindo.id, clientId);
      log(LogEventType.Deleted, excluindo, "escala_escala", excluindo.id, "info");
      notify("Escala excluída!", "success");
      loadData();
    } catch (e: any) {
      notify(e?.message || "Erro ao excluir escala", "error");
    } finally {
      setExcluindo(null);
    }
  };

  // R34: só Administrador de Escala transfere — sem gate de papel na tela,
  // igual ao resto da Escala; quem não pode recebe o erro do backend.
  const onTransferirResponsavel = async (novoResponsavelId: string) => {
    if (!transferindo?.id) return;
    try {
      const result = await transferirResponsavel(transferindo.id, novoResponsavelId, clientId);
      log(LogEventType.Updated, result, "escala_escala", transferindo.id, "info");
      notify("Responsável transferido!", "success");
      setTransferindo(null);
      loadData();
    } catch (e: any) {
      notify(e?.message || "Erro ao transferir responsável", "error");
    }
  };

  // Equipe (docs/escala/05-plano.md D2): carrega a equipe atual
  // (obter_escala), os colaboradores da mesma loja da escala e quem já está
  // em outra escala REGULAR ativa (listar_vinculos_regulares) antes de abrir
  // o modal — evita mostrar as colunas vazias/sem trava por um instante e
  // depois "pular" quando os dados chegam.
  const handleVincular = async (escala: Escala) => {
    if (!escala.id) return;
    setCarregandoVinculoId(escala.id);
    try {
      const [detalhe, colaboradoresDaLojaResult, vinculosRegularesResult] = await Promise.all([
        obterEscala(escala.id, clientId),
        listarTodosColaboradores({ loja_id: escala.loja_id }, clientId),
        listVinculosRegulares(clientId),
      ]);
      setColaboradoresDaLoja(colaboradoresDaLojaResult);
      setVinculosRegulares(vinculosRegularesResult ?? []);
      setVinculoInitialData({
        colaborador_ids: detalhe.colaborador_ids ?? [],
        estrategicos: detalhe.estrategicos ?? [],
        dias_cheios_fixos: detalhe.dias_cheios_fixos ?? [],
        turmas: detalhe.turmas ?? [],
      });
      // O detalhe acabou de ser lido: o modal filtra a equipe pelas cargas que a escala tem agora.
      setVinculando(detalhe);
    } catch (e: any) {
      notify(e?.message || "Erro ao carregar equipe da escala", "error");
    } finally {
      setCarregandoVinculoId(null);
    }
  };

  const handleCloseVinculo = () => {
    setVinculando(null);
    setVinculoInitialData(null);
    setColaboradoresDaLoja([]);
    setVinculosRegulares([]);
  };

  const onSaveVinculos = async (values: Partial<EscalaVinculoValues>) => {
    if (!vinculando?.id) return;
    const erroValidacao = validarVinculoValues(values, vinculando.tipo, colaboradoresDaLoja);
    if (erroValidacao) {
      notify(erroValidacao, "error");
      return;
    }
    try {
      await saveVinculos(
        {
          escala_id: vinculando.id,
          colaborador_ids: values.colaborador_ids ?? [],
          estrategicos: values.estrategicos ?? [],
          dias_cheios_fixos: values.dias_cheios_fixos ?? [],
          turmas: values.turmas ?? [],
        },
        clientId,
      );
      log(LogEventType.Updated, values, "escala_escala_colaborador", vinculando.id, "info");
      notify("Equipe vinculada!", "success");
      handleCloseVinculo();
    } catch (e: any) {
      notify(e?.message || "Erro ao vincular equipe", "error");
    }
  };

  // A equipe pro modal de geração é só quem já está vinculado a esta escala
  // (obter_escala's colaborador_ids), não a loja inteira — é o universo que
  // o backend aceita em folgas_compensatorias (validateFolgasCompensatorias,
  // internal/escala/competencia.go recusa quem não estiver na equipe).
  const handleGerar = async (escala: Escala) => {
    if (!escala.id) return;
    setCarregandoGerarId(escala.id);
    try {
      const [detalhe, colaboradoresDaLoja, ultimaGerada] = await Promise.all([
        obterEscala(escala.id, clientId),
        listarTodosColaboradores({ loja_id: escala.loja_id }, clientId),
        // A jornada da última geração vem preenchida; sem ela, o campo abre em branco.
        listGeradas({ escala_id: escala.id, page: 1, page_size: 1 }, clientId).then(
          (r) => r.items[0],
          () => undefined,
        ),
      ]);
      const idsVinculados = new Set(detalhe.colaborador_ids ?? []);
      const equipe = colaboradoresDaLoja.filter((c) => c.id && idsVinculados.has(c.id));
      // Como separarPorCarga (gerar.go): numa escala com carga, quem não tem uma das cargas dela
      // fica de fora da geração, e quem estaria nela é avisado.
      const temCarga = (c: Colaborador) =>
        !detalhe.com_carga_horaria || (detalhe.cargas_horarias_ids ?? []).includes(c.carga_horaria_id ?? "");
      setColaboradoresDaEquipe(equipe.filter(temCarga));
      setSemCargaDaEquipe(equipe.filter((c) => !temCarga(c) && c.ativo && c.entra_em_escala));
      setVinculadosSemCarga(equipe.length > 0 && !equipe.some(temCarga));
      setJornadaAnterior(ultimaGerada?.jornada_semanal);
      setGerando({ ...escala, com_carga_horaria: !!detalhe.com_carga_horaria });
    } catch (e: any) {
      notify(e?.message || "Erro ao carregar equipe da escala", "error");
    } finally {
      setCarregandoGerarId(null);
    }
  };

  const handleCloseGerar = () => {
    setGerando(null);
    setColaboradoresDaEquipe([]);
    setSemCargaDaEquipe([]);
    setVinculadosSemCarga(false);
    setJornadaAnterior(undefined);
  };

  // Devolve os impedimentos pro modal exibir (a competência não fecha, nada
  // foi persistido — 01-dominio.md pré-condição 7) sem fechar; devolve null
  // e fecha em qualquer outro desfecho, sucesso ou erro de precondição —
  // erro de precondição já vem em português do backend (mesmo padrão de
  // onSaveVinculos), só que aqui o modal continua aberto pro usuário
  // corrigir a competência/quantidade em vez de reabrir do zero.
  const onSubmitGerar = async (values: GerarValues): Promise<Impedimento[] | null> => {
    if (!gerando?.id) return null;

    // Mesmas validações de internal/escala/precondicoes.go e
    // internal/escala/competencia.go, replicadas no cliente — sem isso, um
    // campo vazio ou duplicado só aparece depois de uma ida e volta ao
    // servidor.
    const erroValidacao = validarGerarValues(values);
    if (erroValidacao) {
      notify(erroValidacao, "error");
      return null;
    }
    // Mesma trava de checarPrecondicoes: na escala com carga, sobrou ninguém com uma das cargas.
    if (vinculadosSemCarga) {
      notify("Nenhum colaborador vinculado tem uma das cargas horárias da escala", "error");
      return null;
    }

    try {
      const resultado = await gerarEscala({ escala_id: gerando.id, ...values }, clientId);
      if (resultado.impedimentos && resultado.impedimentos.length > 0) {
        return resultado.impedimentos;
      }
      if (!resultado.gerada) return null;
      log(LogEventType.Created, resultado.gerada, "escala_gerada", resultado.gerada.id, "info");
      const qtdOcorrencias = resultado.ocorrencias?.length ?? 0;
      const semCarga = resultado.sem_carga ?? [];
      const avisoSemCarga = semCarga.length
        ? ` ${semCarga.length} colaborador(es) sem carga desta escala ficaram de fora: ${semCarga.map((c) => c.nome).join(", ")}.`
        : "";
      const avisos = resultado.avisos ?? [];
      const domingos = avisos.filter((a) => a.regra === REGRA_DOMINGO_ACIMA_DO_TETO).length;
      const diasAbaixoDoMinimo = avisos.filter((a) => a.regra === REGRA_PRESENCA_ABAIXO_DO_MINIMO).length;
      const seguidas = avisos.filter((a) => a.regra === REGRA_FOLGAS_SEGUIDAS).length;
      const domingosDoGrupo = new Set(
        avisos.filter((a) => a.regra === REGRA_GRUPO_DE_FOLGA_POR_LEI).flatMap((a) => a.datas ?? [a.data]),
      ).size;
      const pessoasAcima = new Set(avisos.filter((a) => a.regra === REGRA_HORAS_DA_SEMANA).map((a) => a.colaborador_id)).size;
      const cargasSemNinguem = avisos.filter((a) => a.regra === REGRA_CARGA_SEM_NINGUEM).length;
      const avisoDomingo = domingos
        ? ` ${domingos} domingo(s) com mais folga que o teto, porque a lei obrigou: confira no espelho.`
        : "";
      const avisoMinimo = diasAbaixoDoMinimo
        ? ` ${diasAbaixoDoMinimo} dia(s) com menos gente trabalhando que o mínimo: confira no espelho.`
        : "";
      const avisoSeguidas = seguidas ? ` ${seguidas} caso(s) de folgas seguidas: confira no espelho.` : "";
      const avisoGrupo = domingosDoGrupo
        ? ` ${domingosDoGrupo} domingo(s) com o grupo estratégico de folga junto, porque a lei obrigou: confira no espelho.`
        : "";
      const avisoHoras = pessoasAcima ? ` ${pessoasAcima} pessoa(s) com semana acima da jornada: confira no espelho.` : "";
      const avisoCargas = cargasSemNinguem
        ? ` ${cargasSemNinguem} carga(s) horária(s) sem ninguém trabalhando em algum dia: confira no espelho.`
        : "";
      notify(
        (qtdOcorrencias > 0
          ? `Escala gerada com ${qtdOcorrencias} ocorrência(s). Abra o espelho e valide para revisar.`
          : "Escala gerada!") +
          avisoSemCarga +
          avisoDomingo +
          avisoMinimo +
          avisoSeguidas +
          avisoGrupo +
          avisoHoras +
          avisoCargas,
        qtdOcorrencias > 0 || semCarga.length > 0 || avisos.length > 0 ? "warning" : "success",
      );
      handleCloseGerar();
      return null;
    } catch (e: any) {
      notify(e?.message || "Erro ao gerar escala", "error");
      return null;
    }
  };

  if (!carregouAlgumaVez) return <Sync />;

  return (
    <>
      <CrudToolbar>
        <FilterModal activeCount={activeFilterCount} onApply={applyFilters} onClear={handleClearFilters}>
          <TextField
            label="Nome"
            size="small"
            fullWidth
            value={inputValues.nome}
            onChange={(e) => setInputValues((v) => ({ ...v, nome: e.target.value }))}
          />
          <SelectEntidade
            label="Loja"
            options={lojas}
            value={inputValues.loja_id}
            onChange={(id) => setInputValues((v) => ({ ...v, loja_id: id ?? "" }))}
            getLabel={lojaLabel}
            size="small"
          />
        </FilterModal>
        <Button variant="contained" startIcon={<Add />} onClick={handleCreate}>
          Adicionar escala
        </Button>
      </CrudToolbar>

      <DataTable
        data={escalas}
        columns={[
          { key: "nome", label: "Nome" },
          { key: "loja_id", label: "Loja", render: (e) => rotuloLoja(lojas, e.loja_id) },
          { key: "tipo", label: "Tipo", render: (e) => EscalaTipoLabels[e.tipo] },
          { key: "caracteristica", label: "Característica", render: (e) => (e.caracteristica ? EscalaCaracteristicaLabels[e.caracteristica] : "") },
          { key: "turno", label: "Turno" },
          { key: "total_colaboradores", label: "Colaboradores", render: (e) => String(e.total_colaboradores ?? 0) },
        ]}
        total={total}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
        actions={(escala) => (
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <IconButton
              aria-label={`Equipe de ${escala.nome}`}
              onClick={() => handleVincular(escala)}
              disabled={carregandoVinculoId === escala.id}
            >
              <Group />
            </IconButton>
            <IconButton
              aria-label={`Gerar escala de ${escala.nome}`}
              onClick={() => handleGerar(escala)}
              disabled={carregandoGerarId === escala.id}
            >
              <PlayArrow />
            </IconButton>
            <IconButton aria-label={`Transferir responsável de ${escala.nome}`} onClick={() => setTransferindo(escala)}>
              <SwapHoriz />
            </IconButton>
            <IconButton aria-label={`Editar ${escala.nome}`} onClick={() => handleEdit(escala)}>
              <Edit />
            </IconButton>
            <IconButton aria-label={`Excluir ${escala.nome}`} onClick={() => setExcluindo(escala)} color="error">
              <Delete />
            </IconButton>
          </Stack>
        )}
      />

      <CrudModal<
        Escala,
        { lojas: Loja[]; cargas: CargaHoraria[]; cargasSalvas: string[]; onToggleLiberarForaPrazo: typeof handleToggleLiberarForaPrazo }
      >
        open={open}
        onClose={handleClose}
        title={editing ? "Editar escala" : "Adicionar escala"}
        initialData={editing}
        onSubmit={onSave}
        Form={EscalaForm}
        extraFormProps={{
          lojas,
          cargas,
          cargasSalvas: editing?.cargas_horarias_ids ?? [],
          onToggleLiberarForaPrazo: handleToggleLiberarForaPrazo,
        }}
      />

      <EscalaResponsavelModal
        escala={transferindo}
        clientId={clientId}
        onClose={() => setTransferindo(null)}
        onSubmit={onTransferirResponsavel}
      />

      <ConfirmModal
        open={!!excluindo}
        onClose={() => setExcluindo(null)}
        onConfirm={onExcluirConfirm}
        title="Exclusão"
        btnText="Confirmar"
        text={`Deseja excluir a escala ${excluindo?.nome}? Escalas com geração viva não podem ser excluídas.`}
      />

      <EscalaVinculoModal
        escala={vinculando}
        initialData={vinculoInitialData}
        colaboradores={colaboradoresDaLoja}
        vinculosRegulares={vinculosRegulares}
        onClose={handleCloseVinculo}
        onSubmit={onSaveVinculos}
      />

      <EscalaGerarModal
        escala={gerando}
        colaboradores={colaboradoresDaEquipe}
        semCarga={semCargaDaEquipe}
        jornadaAnterior={jornadaAnterior}
        onClose={handleCloseGerar}
        onSubmit={onSubmitGerar}
      />
    </>
  );
}
